import { z } from "zod";

const boolString = () =>
  z
    .string()
    .nullish()
    .transform((arg) => arg?.toLowerCase() === "true");

const updateBoolString = () =>
  z
    .union([z.string(), z.null()])
    .transform((arg) => arg?.toLowerCase() === "true")
    .optional();

const numberString = () =>
  z
    .string()
    .nullish()
    .transform((arg) => (arg ? Number(arg) : undefined))
    .refine(
      (val) => val === undefined || !Number.isNaN(val),
      "Must be a valid number",
    );

/**
 * Global boolean feature flags. When adding one, classify it (C173):
 *
 *   - **Capability flag** — gates a privileged or safety-relevant *action*
 *     (e.g. `allowSelfValidation` lets a user bypass double-entry). A capability
 *     flag MUST be enforced server-side at the action itself, not only in the
 *     client. `allowSelfValidation` is checked in
 *     `packages/services/results/validate.ts`; a client check alone is a bypass.
 *   - **Variant flag** — selects a UI or render variant with no privileged
 *     action behind it (e.g. `enableLabelsV2` picks a render path,
 *     `hideTestEnvironmentBadge` hides a badge). Client-only is fine;
 *     there is nothing to enforce on the server. The server reads a variant
 *     flag only when it owns the render (e.g.
 *     `enableSerologyReportResultSampleId` in `@blis/reports`).
 *
 * The broader settings remodel (per-key schemas, scoping, one read boundary —
 * C168/C170/C172) is the Phase 3 Policy program, not a per-flag concern here.
 */
export const featureFlagSchema = z.object({
  allowSelfValidation: boolString(),
  hideTestEnvironmentBadge: boolString(),
  enableLabelsV2: boolString(),
  enableSerologyReportResultSampleId: boolString(),
  // Capability flag (ADR-0004): when on, a hold hard-blocks final report
  // release. Server-enforced in the report service.
  holdBlocksFinalReport: boolString(),
  // Capability flag (BLIS-1473): gates the entire QC module — nav, routes, and
  // the `qc` tRPC procedures. Off by default; server-enforced at the qc router
  // (BLIS-1478) so a disabled module rejects calls, not merely hides the UI.
  enableQcModule: boolString(),
  // Rollout flag (BLIS-1518): selects executor-owned desired-state reconcile
  // instead of the temporary legacy algorithm-owned lifecycle path.
  enableReflexReconcile: boolString(),
  // Rollout flag (BLIS-1545): atomic shadow writes of immutable result versions
  // while legacy columns remain the production read authority. Off until the
  // expand-only schema is deployed; server-enforced at the Results writer.
  enableResultHistoryShadow: boolString(),
});

export const featureFlagUpdateSchema = z.object({
  allowSelfValidation: updateBoolString(),
  hideTestEnvironmentBadge: updateBoolString(),
  enableLabelsV2: updateBoolString(),
  enableSerologyReportResultSampleId: updateBoolString(),
  holdBlocksFinalReport: updateBoolString(),
  enableQcModule: updateBoolString(),
  enableReflexReconcile: updateBoolString(),
  enableResultHistoryShadow: updateBoolString(),
});

/**
 * Parsed label printer settings. Not enforced on `settings.update` today —
 * the admin form validates writes, and Labels V2 clamps at emit time.
 * Keys `printerPrintSpeed` / `printerDarkness` are stored as raw strings.
 */
export const labelSettingsSchema = z.object({
  printerDpi: numberString(),
  printerOffsetLeftMm: numberString(),
  printerOffsetTopMm: numberString(),
});

export type FeatureFlagsInput = z.input<typeof featureFlagSchema>;
export type FeatureFlags = z.infer<typeof featureFlagSchema>;
export type FeatureFlag = keyof FeatureFlags;

export type LabelSettingsInput = z.input<typeof labelSettingsSchema>;
export type LabelSettings = z.infer<typeof labelSettingsSchema>;
export type LabelSetting = keyof LabelSettings;

/**
 * Billing policy (BLIS-1788). Missing/empty means Off — a fully cancelled
 * instruction does not bill, including on-run extras. Only the stored string
 * `true` charges cancelled in-progress tests. This is not a feature flag.
 */
export const CHARGE_CANCELLED_IN_PROGRESS_TESTS_SETTING =
  "chargeCancelledInProgressTests";

export function parseChargeCancelledInProgressTests(
  value: string | null | undefined,
): boolean {
  return value?.trim().toLowerCase() === "true";
}

/** JSON array of footer lines for printed worklists; empty/absent = no footer. */
export const worklistFooterLinesSchema = z
  .string()
  .nullish()
  .transform((val) => {
    if (!val) {
      return [];
    }
    try {
      return z.array(z.string()).parse(JSON.parse(val));
    } catch {
      return [];
    }
  });

/** Split textarea content into stored footer lines, preserving intentional blank lines. */
export function splitWorklistFooterLinesText(text: string): string[] {
  const lines = text.split(/\r?\n/).map((line) => line.trimEnd());
  while (lines.length > 0 && lines.at(-1)?.trim().length === 0) {
    lines.pop();
  }
  return lines;
}

/** Serialize textarea content for the settings table; null clears the footer. */
export function formatWorklistFooterLinesForStorage(
  text: string,
): string | null {
  const lines = splitWorklistFooterLinesText(text);
  if (lines.every((line) => line.trim().length === 0)) {
    return null;
  }
  return JSON.stringify(lines);
}

/** Render stored footer lines back into textarea content. */
export function formatWorklistFooterLinesForEditing(
  value: string | null | undefined,
): string {
  return worklistFooterLinesSchema.parse(value).join("\n");
}

/** Explicit org-id allowlist. Empty or absent = Case type stays hidden on form-new create. */
export const CASE_TYPE_REQUIRED_ORG_IDS_SETTING = "caseTypeRequiredOrgIds";

const orgIdArraySchema = z.array(z.number().int().positive());

/** JSON array of org ids; empty/absent/invalid = no orgs. */
export const caseTypeRequiredOrgIdsSchema = z
  .string()
  .nullish()
  .transform((val) => {
    if (!val) {
      return [];
    }
    try {
      return orgIdArraySchema.parse(JSON.parse(val));
    } catch {
      return [];
    }
  });

export function parseCaseTypeRequiredOrgIds(
  value: string | null | undefined,
): number[] {
  return caseTypeRequiredOrgIdsSchema.parse(value);
}

export function isCaseTypeRequiredForOrg(
  orgId: number | null | undefined,
  requiredOrgIds: readonly number[],
): boolean {
  if (orgId == null || !Number.isFinite(orgId)) {
    return false;
  }
  return requiredOrgIds.includes(orgId);
}

export function isCaseTypeMissingWhenRequired(
  orgId: number | null | undefined,
  caseType: string | null | undefined,
  requiredOrgIds: readonly number[],
): boolean {
  return isCaseTypeRequiredForOrg(orgId, requiredOrgIds) && !caseType;
}

/** Serialize a JSON org-id array for the settings table. Empty becomes `[]`. */
export function formatCaseTypeRequiredOrgIdsForStorage(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return "[]";
  }
  return JSON.stringify(orgIdArraySchema.parse(JSON.parse(trimmed)));
}
